import {
  Alert,
  AlertActionLink,
  FormAlert,
  PageSection,
  Tab,
  Tabs,
  TabTitleText,
  Title,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { PatientList } from '@app/components'
import type { PatientActionItem } from '@app/components/patient/patient-list/patient-list.interface'
import { SelectMultiplePatientModal } from '@app/components/patient/patient-search/select-multiple-patient-modal'
import { useFeatureFlags } from '@app/domain/feature-flags/feature-flags.hooks'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import type {
  IPatient,
  IPatientId,
} from '@app/domain/patient/patient.interface'
import {
  useProgramEnrolmentsWithPatients,
  useBulkEnrolment,
  useUpdateEnrolmentStartDate,
} from '@app/domain/program/program.hooks'
import type {
  IProgram,
  IProgramEnrolment,
} from '@app/domain/program/program.interface'
import {
  ProgramEnrolmentStatusId,
  ProgramInteractionTypeId,
} from '@app/domain/program/program.interface'
import { formatIsoDateTimeOffset } from '@app/i18n/formats'
import type { ISortDirection } from '@app/lib/sorting'
import { InfoTooltip } from '@app/utils/info-tooltip-icon'

import { BulkEnrolPatientsInProgram } from './bulk-enrol'
import { CommunicationStatusDetails } from './communication-status-detail'
import { DiscontinueProgramModal } from './discontinue-program-modal'
import { EditEnrolmentModal } from './edit-enrolment'

interface Props {
  program: IProgram
}

enum TabKeys {
  Enrolment,
  BulkUpload,
}

enum ActionKeys {
  EditEnrolment = 'edit-enrolment',
}

const enrolmentStatusLabelMap: Record<ProgramEnrolmentStatusId, string> = {
  [ProgramEnrolmentStatusId.Enroled]: 'Enroled',
  [ProgramEnrolmentStatusId.Discontinued]: 'Discontinued',
  [ProgramEnrolmentStatusId.Removed]: 'Removed',
}

const actionsByEnrolmentStatus: Record<
  ProgramEnrolmentStatusId,
  PatientActionItem[]
> = Object.values(ProgramEnrolmentStatusId).reduce((acc, status) => {
  acc[status] = [{ key: ActionKeys.EditEnrolment, label: 'Edit Enrolment' }]
  return acc
}, {} as Record<ProgramEnrolmentStatusId, PatientActionItem[]>)

export const ProgramPatientList = ({ program }: Props) => {
  const history = useHistory()
  const {
    addPatientsToProgram,
    getPatients,
    getProgramEnrolments,
    patients,
    programEnrolments,
    searchOptions,
    setSearchOptions,
    setPageNumber,
    setRecordsPerPage,
    pageNumber,
    recordsPerPage,
    totalNumberOfRecords,
    updateEnrolmentStatus,
  } = useProgramEnrolmentsWithPatients(program?.programId)

  const featureFlags = useFeatureFlags()
  const [activeTabKey, setActiveTabKey] = useState<number>(TabKeys.Enrolment)
  const [file, setFile] = useState<File | undefined>(undefined)
  const [filename, setFilename] = useState('')

  const {
    uploadEnrolmentFile,
    numberEnrolled,
    numberUpdated,
    uploadStatus,
    isLoading,
    uploadError,
    enrolmentErrors,
    clearStatus,
  } = useBulkEnrolment(program.programId)

  const setActiveTab = useCallback((_, selectedTab) => {
    setActiveTabKey(selectedTab)
  }, [])

  const {
    birthSex,
    dateOfBirth,
    mrn,
    patientName,
    deliverable,
    sortField,
  } = searchOptions

  const [sortDirection, setSortDirection] = useState<ISortDirection>(null)

  const [isSearchModalOpen, setIsSearchModalOpen] = useState<boolean>(false)
  const [isDiscontinueModalOpen, setIsDiscontinueModalOpen] = useState<boolean>(
    false,
  )
  const [selectedPatients, setSelectedPatients] = useState<IPatient[]>([])
  const [
    programHasRemoteInteraction,
    setProgramHasRemoteInteraction,
  ] = useState(false)

  const [isEditEnrolmentModalOpen, setIsEditEnrolmentModalOpen] = useState(
    false,
  )
  const [
    selectedEnrolment,
    setSelectedEnrolment,
  ] = useState<IProgramEnrolment | null>(null)

  const { updateStartDate } = useUpdateEnrolmentStartDate(program?.programId)

  const patientIdToProgramEnrolment: Record<
    IPatientId,
    IProgramEnrolment
  > = useMemo(() => {
    if (programEnrolments) {
      return programEnrolments.reduce<Record<IPatientId, IProgramEnrolment>>(
        (map, enrolment) => {
          map[enrolment.patientId] = enrolment
          return map
        },
        {},
      )
    } else {
      return {}
    }
  }, [programEnrolments])

  const onRefresh = useCallback(() => {
    getPatients()
    getProgramEnrolments()
    setSelectedEnrolment(null)
  }, [getPatients, getProgramEnrolments])

  useEffect(() => {
    setProgramHasRemoteInteraction(
      !!program?.interactions[ProgramInteractionTypeId.RemoteInteraction],
    )
  }, [program])

  useEffect(() => {
    getPatients()
  }, [searchOptions, pageNumber, getPatients])

  const onBirthSexFilterUpdated = useCallback(
    (updatedBirthSex: string[]) => {
      setSelectedPatients([])
      setSearchOptions({
        ...searchOptions,
        birthSex: updatedBirthSex,
      })
      setPageNumber(1)
    },
    [searchOptions, setPageNumber, setSearchOptions],
  )

  const onDateOfBirthFilterUpdated = useCallback(
    (updatedDateOfBirth: string) => {
      setSelectedPatients([])
      setSearchOptions({
        ...searchOptions,
        dateOfBirth: updatedDateOfBirth,
      })
      setPageNumber(1)
    },
    [searchOptions, setPageNumber, setSearchOptions],
  )

  const onMrnFilterUpdated = useCallback(
    (updatedMrn: string) => {
      setSearchOptions({
        ...searchOptions,
        mrn: updatedMrn,
      })
      setPageNumber(1)
    },
    [searchOptions, setPageNumber, setSearchOptions],
  )

  const onNameFilterUpdated = useCallback(
    (updatedName: string) => {
      setSearchOptions({
        ...searchOptions,
        patientName: updatedName,
      })
      setPageNumber(1)
    },
    [searchOptions, setPageNumber, setSearchOptions],
  )

  const onDeliverableFilterUpdated = useCallback(
    (updatedDeliverable?: boolean) => {
      setSearchOptions({
        ...searchOptions,
        deliverable: updatedDeliverable,
      })
      setPageNumber(1)
    },
    [searchOptions, setPageNumber, setSearchOptions],
  )

  const onClearAllFilters = useCallback(() => {
    setSearchOptions({
      ...searchOptions,
      birthSex: [],
      dateOfBirth: null,
      mrn: null,
      patientName: null,
      deliverable: undefined,
    })

    setPageNumber(1)
  }, [searchOptions, setPageNumber, setSearchOptions])

  const onSetPage = useCallback(
    (updatedPageNumber: number) => {
      setPageNumber(updatedPageNumber)
      getPatients()
    },
    [getPatients, setPageNumber],
  )

  const onPerPageSelect = useCallback(
    (updatedRecordsPerPage: number) => {
      setRecordsPerPage(updatedRecordsPerPage)
      setPageNumber(1)
      getPatients()
    },
    [getPatients, setPageNumber, setRecordsPerPage],
  )

  const onSetSort = useCallback(
    (newField: string, newDirection: ISortDirection) => {
      setPageNumber(1)
      setSortDirection(newDirection)
      setSearchOptions({
        ...searchOptions,
        sortDirection: newDirection,
        sortField: newField,
      })
      getPatients()
    },
    [getPatients, searchOptions, setPageNumber, setSearchOptions],
  )

  const cancelEnrolPatient = useCallback(() => {
    setIsSearchModalOpen(false)
  }, [])

  const handleEnrolPatient = useCallback(() => {
    setIsSearchModalOpen(true)
  }, [])

  const handleUpdateProgramEnrolmentStatus = useCallback(
    (
      patientIds: IPatientId[],
      status: ProgramEnrolmentStatusId,
      statusReason?: string,
      statusReasonDescription?: string,
    ) => {
      updateEnrolmentStatus(
        patientIds,
        status,
        statusReason,
        statusReasonDescription,
      ).then(() => {
        setIsDiscontinueModalOpen(false)
        onRefresh()
      })
    },
    [updateEnrolmentStatus, onRefresh],
  )

  const cancelDiscontinuePatient = useCallback(() => {
    setIsDiscontinueModalOpen(false)
  }, [])

  const confirmDiscontinuePatient = useCallback(
    (statusReason: string, statusReasonDescription: string) => {
      handleUpdateProgramEnrolmentStatus(
        selectedPatients.map((it) => it.id),
        ProgramEnrolmentStatusId.Discontinued,
        statusReason,
        statusReasonDescription,
      )
    },
    [handleUpdateProgramEnrolmentStatus, selectedPatients],
  )

  const confirmRemovePatient = useCallback(() => {
    handleUpdateProgramEnrolmentStatus(
      selectedPatients.map((it) => it.id),
      ProgramEnrolmentStatusId.Removed,
    )
    setIsEditEnrolmentModalOpen(false)
  }, [handleUpdateProgramEnrolmentStatus, selectedPatients])

  const confirmContinueEnrolment = useCallback(() => {
    handleUpdateProgramEnrolmentStatus(
      selectedPatients.map((it) => it.id),
      ProgramEnrolmentStatusId.Enroled,
    )
  }, [handleUpdateProgramEnrolmentStatus, selectedPatients])

  const handleAddContactInformation = useCallback(
    (patientId: string) => {
      history.push(`/clinic/patient/${patientId}`)
    },
    [history],
  )

  const validateSelectedPatient = useCallback(
    (patient: IPatient) => {
      if (!patient.contactInformation && programHasRemoteInteraction) {
        return (
          <FormAlert>
            <Alert
              variant="danger"
              title={`This program has a remote interaction. Patient ${patient.medicalRecordNumber} is missing contact information. Please add contact information to continue.`}
              aria-live="polite"
              isInline
              actionLinks={
                <AlertActionLink
                  onClick={() => handleAddContactInformation(patient.id)}
                >
                  Add Contact Information
                </AlertActionLink>
              }
            />
            <br />
          </FormAlert>
        )
      } else {
        return null
      }
    },
    [handleAddContactInformation, programHasRemoteInteraction],
  )

  const handleActionSelected = useCallback(
    (key: string, patient: IPatient) => {
      const enrolment = patientIdToProgramEnrolment[patient.id]

      if (!enrolment) {
        return
      }

      switch (key) {
        case ActionKeys.EditEnrolment: {
          setSelectedPatients([patient])
          setSelectedEnrolment(enrolment)
          setIsEditEnrolmentModalOpen(true)
          break
        }
      }
    },
    [patientIdToProgramEnrolment],
  )

  const handleEnrolPatients = useCallback(
    (pts: IPatient[], startDate: string) => {
      addPatientsToProgram(
        pts.map((it) => it.id),
        startDate,
      ).then(() => {
        setIsSearchModalOpen(false)
      })
    },
    [addPatientsToProgram],
  )

  const handleEditProgramStartTime = useCallback(
    async (startDate: string) => {
      await updateStartDate(selectedEnrolment?.patientId, startDate)
      setIsEditEnrolmentModalOpen(false)
      onRefresh()
    },
    [selectedEnrolment, updateStartDate, onRefresh],
  )

  const handlePatientSelected = useCallback(
    (patient: IPatient) => {
      history.push(`/clinic/patient/${patient.id}`)
    },
    [history],
  )

  const handleBuildActions = useCallback(
    (patient: IPatient) => {
      if (patientIdToProgramEnrolment[patient.id] !== undefined) {
        const enrolment = patientIdToProgramEnrolment[patient.id]
        const actionsByType = actionsByEnrolmentStatus[enrolment.status] ?? []
        return actionsByType
      }

      return []
    },
    [patientIdToProgramEnrolment],
  )

  const handleBuildColumns = useCallback(
    (patient: IPatient) => {
      if (patientIdToProgramEnrolment[patient.id] !== undefined) {
        const enrolment = patientIdToProgramEnrolment[patient.id]
        return [
          <>
            {enrolment.startDate
              ? `${formatIsoDateTimeOffset(enrolment.startDate)}`
              : ''}
          </>,
          <>{enrolmentStatusLabelMap[enrolment.status]}</>,
          featureFlags.isFeatureFlagEnabled(FeatureFlagId.RemoteInteraction) ? (
            <CommunicationStatusDetails
              emailAddress={enrolment.emailAddress}
              consentsToEmail={enrolment.consentsToEmail}
              phoneNumber={enrolment.phoneNumber}
              phoneNumberType={enrolment.phoneNumberType}
              consentsToSms={enrolment.consentsToSms}
            />
          ) : undefined,
          <>
            {enrolment.statusReason}{' '}
            {enrolment.statusReasonDescription ? (
              <InfoTooltip content={enrolment.statusReasonDescription} />
            ) : null}
          </>,
        ].filter((it) => it !== undefined)
      }

      return [null, null]
    },
    [featureFlags, patientIdToProgramEnrolment],
  )

  const handleFileChange = useCallback(
    (
      fileName: string,
      _event:
        | React.ChangeEvent<HTMLInputElement>
        | React.DragEvent<HTMLElement>,
      f: File,
    ) => {
      setFile(f)
      setFilename(fileName)
    },
    [],
  )

  const handleClear = useCallback(() => {
    clearStatus()
    setFile(undefined)
    setFilename('')
  }, [clearStatus])

  const handleUpload = useCallback(() => {
    if (file) {
      uploadEnrolmentFile(file)
    }
  }, [file, uploadEnrolmentFile])

  useEffect(() => {
    if (uploadStatus === 'success' && !isLoading) {
      onRefresh()
    }
  }, [uploadStatus, isLoading, onRefresh])

  const generateBulkEnrolmentMessage = () => {
    const messages: string[] = []

    if (numberEnrolled > 0) {
      messages.push(
        `${numberEnrolled} enrolment${numberEnrolled !== 1 ? 's' : ''} added.`,
      )
    } else {
      messages.push(`No enrolments were added.`)
    }

    if (numberUpdated > 0) {
      messages.push(
        `${numberUpdated} enrolment${numberUpdated !== 1 ? 's' : ''} updated.`,
      )
    }

    const parsingErrors = enrolmentErrors.filter(
      (error) => error.type === 'parsing-error',
    )
    const otherErrors = enrolmentErrors.filter(
      (error) => error.type !== 'parsing-error',
    )

    return (
      <>
        <Alert variant="success" title="Upload successful" isInline>
          <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
            {messages.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
        </Alert>

        {otherErrors.length > 0 && (
          <Alert variant="warning" title="Some enrolments failed" isInline>
            <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
              {otherErrors.map((error, index) => (
                <li key={`error-${index}`}>
                  Row {error.rowIndex} -{' '}
                  {error.type === 'invalid-mrn'
                    ? `Invalid MRN: ${error.mrn}`
                    : `${error.type
                        .replace('-', ' ')
                        .replace(/^./, (str) => str.toUpperCase())}`}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {parsingErrors.length > 0 && (
          <Alert variant="danger" title="File format issue" isInline>
            <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
              {parsingErrors.map((error, index) => (
                <li key={`parsing-${index}`}>
                  Row {error.rowIndex} -{' '}
                  {error.type
                    .replace('-', ' ')
                    .replace(/^./, (str) => str.toUpperCase())}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </>
    )
  }

  const remoteInteractionEnabled = featureFlags.isFeatureFlagEnabled(
    FeatureFlagId.RemoteInteraction,
  )
  const patientListColumnHeaders = [
    { title: 'Start Date', value: 'start_date', sortable: false },
    { title: 'Status', value: 'status', sortable: false },
    ...(remoteInteractionEnabled
      ? [
          {
            title: 'Communication Status',
            value: 'communication_status',
            sortable: true,
          },
        ]
      : []),
    { title: 'Reason', value: 'reason', sortable: false },
  ]

  return (
    <>
      <SelectMultiplePatientModal
        confirmButtonLabel={'Enrol'}
        defaultSearchOptions={{ notInProgramId: program?.programId }}
        isModalOpen={isSearchModalOpen}
        onCancel={cancelEnrolPatient}
        onPatientsFound={handleEnrolPatients}
        validateSelectedPatient={validateSelectedPatient}
      />

      <DiscontinueProgramModal
        isModalOpen={isDiscontinueModalOpen}
        onCancel={cancelDiscontinuePatient}
        onConfirm={confirmDiscontinuePatient}
      />

      {selectedEnrolment ? (
        <EditEnrolmentModal
          key={selectedEnrolment?.patientId}
          isModalOpen={isEditEnrolmentModalOpen}
          onClose={() => setIsEditEnrolmentModalOpen(false)}
          enrolment={selectedEnrolment}
          onUpdateStartDate={handleEditProgramStartTime}
          onDiscontinue={confirmDiscontinuePatient}
          onRemove={confirmRemovePatient}
          onContinue={confirmContinueEnrolment}
        />
      ) : null}

      <Title headingLevel="h2" size="xl">
        Patient Enrolment
      </Title>

      <PageSection>
        <Tabs
          activeKey={activeTabKey}
          onSelect={setActiveTab}
          aria-label="Enrolment Tabs"
        >
          <Tab
            eventKey={TabKeys.Enrolment}
            title={<TabTitleText>Enrol Patients</TabTitleText>}
          />
          <Tab
            eventKey={TabKeys.BulkUpload}
            title={<TabTitleText>Bulk Enrol</TabTitleText>}
          />
        </Tabs>

        {activeTabKey === TabKeys.Enrolment ? (
          <PatientList
            buildActions={handleBuildActions}
            buildColumns={handleBuildColumns}
            columnHeaders={patientListColumnHeaders}
            onActionSelected={handleActionSelected}
            onPatientSelected={handlePatientSelected}
            patients={patients}
            onRefresh={onRefresh}
            onClearAllFilters={onClearAllFilters}
            filters={{
              birthSexFilter: birthSex,
              dateOfBirthFilter: dateOfBirth,
              mrnFilter: mrn,
              nameFilter: patientName,
              deliverableFilter: deliverable,
              onBirthSexFilterUpdated,
              onDateOfBirthFilterUpdated,
              onDeliverableFilterUpdated,
              onNameFilterUpdated,
              onMrnFilterUpdated,
            }}
            onPrimaryAction={handleEnrolPatient}
            pagination={{
              onSetPage,
              onPerPageSelect,
              recordsPerPage,
              pageNumber,
              totalNumberOfRecords,
            }}
            primaryActionLabel="Enrol New Patient"
            sorting={{
              sortDirection,
              sortField,
              onSetSort,
            }}
          />
        ) : (
          <>
            {uploadStatus === 'failure' && (
              <Alert variant="danger" title="Upload failed" isInline>
                {uploadError}
              </Alert>
            )}

            {uploadStatus === 'success' && generateBulkEnrolmentMessage()}

            <BulkEnrolPatientsInProgram
              onFileChange={handleFileChange}
              onClear={handleClear}
              onUpload={handleUpload}
              file={file}
              fileName={filename}
            />
          </>
        )}
      </PageSection>
    </>
  )
}
